import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { expenseDetails } from "../../../core/data/json/expenseDetails";
import Table from "../../../core/common/dataTable/index";
import ReactApexChart from "react-apexcharts";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import {
  exportToCSV,
  exportToExcel,
} from "../../../core/common/GlobalFunctions/exportTableData";
import { fetchAllEsicCount, fetchAllEsicDetails, fetchAllEsicMonthlyCount } from "../../../core/data/redux/reports/esic_report/esic.Action";
import { Container, Row, Col, Form } from "react-bootstrap";
import { fetchAllTDSAction, fetchAllTDSCount, fetchAllTDSMonthlyCount } from "../../../core/data/redux/reports/tds_report/tds.Action";




const EmployeeTaxTable = ({ data = [], onSearch }: { data?: any, onSearch: (year: string, month: string) => void; }) => {
  const rows =
    Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

  const [searchEmployee, setSearchEmployee] = useState("");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  // ⬇️ Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const months = [
    { value: "", label: "All Months" },
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const filteredRows = useMemo(() => {
  return rows.filter((item: any) => {
    const emp = item?.Name || "";
    const matchEmployee = emp.toLowerCase().includes(searchEmployee.toLowerCase());

    const itemMonth = item.MonthYear ? item.MonthYear.split("-")[1] : "";
    const itemYear = item.MonthYear ? item.MonthYear.split("-")[0] : "";

    const matchMonth = month ? itemMonth === month : true;
    const matchYear = year ? itemYear === year : true;

    return matchEmployee && matchMonth && matchYear;
  });
}, [searchEmployee, month, year, rows]);


  // ⬇️ Pagination slice
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

  // ⬇️ reset page on search or filter
  useEffect(() => {
    setCurrentPage(1);
  }, [searchEmployee, month, year]);

  return (
    <>
      {/* CUSTOM TABLE STYLES */}
      <style>{`
        .custom-table {
          width: 100%;
          border: 1px solid #ccc;
          border-radius: 8px;
          overflow-y: auto;
          max-height: 520px;
        }
        .table-row {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr 1fr 1fr;
          padding: 10px 14px;
          border-bottom: 1px solid #eee;
          align-items: center;
          font-size: 14px;
        }
        .table-header {
          background: #e5e9ff;
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 10;
        }
      `}</style>

      <div className="w-full max-w-[1600px] mx-auto mt-10 px-4">
        <div className="row mb-4 align-items-end">
          <div className="col-md-3">
            <label className="form-label fw-semibold">Employee Filter</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search Employee"
              value={searchEmployee}
              onChange={(e) => setSearchEmployee(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold">Month</label>
            <select
              className="form-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label fw-semibold">Year</label>
            <select
              className="form-select"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">All Years</option>
              <option value="2023">2023</option>
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>

          <div className="col-md-2">
            <button
              className="btn btn-primary w-100"
              style={{ marginTop: "32px" }}
              onClick={() => onSearch(year, month)}
            >
              🔍 Search
            </button>
          </div>
        </div>

        {/* DATA TABLE */}
        <div className="custom-table">
          <div className="table-row table-header">
            <div>Employee ID</div>
            <div>Name</div>
            <div>Month-Year</div>
            <div>Applicable Income</div>
            <div>Calculated Tax</div>
          </div>

          {currentRows.length > 0 ? (
            currentRows.map((item: any, idx: number) => (
              <div key={idx} className="table-row">
                <div>{item.EmployeeID ?? "-"}</div>
                <div>{item.Name ?? "-"}</div>
                <div>{item.MonthYear ?? "-"}</div>
                <div>{item.ApplicableIncome ?? "0"}</div>
                <div>{item.CalculatedTax ?? "0"}</div>
              </div>
            ))
          ) : (
            <div className="table-row text-center col-span-5">
              No Data Found
            </div>
          )}
        </div>

        {/* ⬇️ PAGINATION UI */}
        {filteredRows.length > 0 && (
          <div className="d-flex justify-content-end mt-3 gap-2">
            <button
              className="btn btn-secondary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              ◀ Prev
            </button>
            <span className="align-self-center fw-semibold">
              Page {currentPage} / {totalPages}
            </span>
            <button
              className="btn btn-secondary"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next ▶
            </button>
          </div>
        )}
      </div>
    </>
  );
};



const TDS_Reports = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedYear, setSelectedYear] = useState("2025");
  const [chartdata, setchartdata] = useState<any[]>([]);
  const [count, setcount] = useState<any>("");
  console.log(count , 'count');
  const [request, setrequest] = useState<any>([]);
  const [employeeName, setEmployeeName] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  
  useEffect(() => {
    dispatch(fetchAllTDSAction({ year, month }))
      .then((res) => {
        setrequest(res.payload.data)
      })
    dispatch(fetchAllTDSMonthlyCount(selectedYear))
      .then((res) => {
        setchartdata(res.payload.data)
      })
    dispatch(fetchAllTDSCount({year , month}))
      .then((res) => {
        setcount(res.payload.data)
      })
  }, []);

  // const data = records ? records : [];

  function filterTDSData(data: any[]) {
  return data.map(item => ({
    name: item.Name || "N/A",
    monthYear: item.MonthYear || "N/A",
    applicableIncome: item.ApplicableIncome || "0.00",
    calculatedTax: item.CalculatedTax || "0.00",
  }));
}


const dataForExport = filterTDSData(request)

  

const handleSearch = (year: string, month: string) => {
  dispatch(fetchAllTDSAction({ year, month })).then((res: any) => {
    setrequest(res.payload.data);
  });
  dispatch(fetchAllTDSCount({year , month}))
      .then((res) => {
        setcount(res.payload.data)
  })
};


  const filterEmployees = (data: any, filters: any) => {
    const { employeeName, month, year } = filters;

    return data.filter((item: any) => {
      const matchEmployee =
        !employeeName ||
        item.Name.toLowerCase().includes(employeeName.toLowerCase());

      const itemMonth = item.MonthYear ? item.MonthYear.split("-")[1] : null;
      const itemYear = item.MonthYear ? item.MonthYear.split("-")[0] : null;

      const matchMonth = !month || itemMonth === month;
      const matchYear = !year || itemYear === year;

      return matchEmployee && matchMonth && matchYear;
    });
  };


  const filteredData = filterEmployees(request, {
    employeeName,
    month,
    year,
  });




  // const data2 = filterExpenseData(data)


  // const filteredData = useMemo(() => {
  //   return filterData(data || [], {}, "CreatedAt", "CreatedAt", selectedSort);
  // }, [dispatch, records, selectedSort, selectedYear]);

  // useEffect(() => {
  //   setTableKey((prevKey) => prevKey + 1);
  // }, [selectedStatus, selectedSort, records, selectedYear]);



  useEffect(() => {
    const expensePerMonth = new Array(12).fill(0);

    if (chartdata && chartdata.length > 0) {
      chartdata.forEach(({ MonthYear, TotalCalculatedTax }: any) => {
        const monthNumber = Number(MonthYear?.split("-")[1]);
        if (monthNumber >= 1 && monthNumber <= 12) {
          expensePerMonth[monthNumber - 1] = parseFloat(TotalCalculatedTax) || 0;
        }
      });
    }

    setexpensechart((prev: any) => ({
      ...prev,
      series: [
        {
          ...prev.series[0],
          data: expensePerMonth,
        },
      ],
    }));
  }, [chartdata, dispatch]);



  useEffect(() => {
    dispatch(fetchAllTDSMonthlyCount(selectedYear))
      .then((res) => {
        setchartdata(res.payload.data)
      })
      dispatch(fetchAllTDSCount({year , month}))
      .then((res) => {
        setcount(res.payload.data)
      })
  }, [dispatch, selectedYear]);


  const handleYearSelect = (year: any) => {
    setSelectedYear(year);
  };



  //New Chart
  const [expensechart, setexpensechart] = useState<any>({
    series: [
      {
        name: "TDS Contribution",
        data: new Array(12).fill(0),
      },
    ],
    chart: {
      height: 190,
      type: "area",
      zoom: { enabled: false },
    },
    colors: ["#FF9F43"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    xaxis: {
      categories: [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
      ],
    },
    yaxis: {
      min: 0,
      max: 10000,
      tickAmount: 5,
      labels: {
        offsetX: -15,
        formatter: (val: number) =>
          val >= 1000 ? (val / 1000).toFixed(1) + "K" : val,
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
    },
  });




  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">TDS Report</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index.html">
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    TDS Report
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() => exportToCSV(dataForExport, "TDS_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToExcel(dataForExport, "TDS_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            {/* Total Exponses */}
            <div className="col-xl-6 d-flex">
              <div className="row flex-fill">
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/total-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Tax Calculated
                          </span>
                          <h5>{count ? count.totalCalculatedTax : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-primary border border-primary"
                        >
                          <span className="text-primary">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +20.01%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/approved-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Applicable Income
                          </span>
                          <h5>{count ? count?.totalApplicableIncome : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-success border border-success"
                        >
                          <span className="text-success">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +17.01%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/pending-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            Total Employee Mapped
                          </span>
                          <h5>{count ? count?.totalEmployees : 0}</h5>
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-skyblue border border-skyblue"
                        >
                          <span className="text-skyblue">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-success fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    +10.13%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 d-flex">
                  <div className="card flex-fill position-relative">
                    <span className="position-absolute start-0 bottom-0">
                      <ImageWithBasePath
                        src="assets/img/reports-img/reject-expense.svg"
                        alt="img"
                        className="img-fluid"
                      />
                    </span>
                    <div className="card-body">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div>
                          <span className="fs-14 fw-normal text-truncate mb-1">
                            {/* Total Benefit_Type */}
                          </span>
                          {/* <h5>{count ? count?.[0].total_esic_benefit_types : 0}</h5> */}
                        </div>
                        <Link
                          to="#"
                          className="avatar avatar-md avatar-rounded bg-transparent-danger border border-danger"
                        >
                          <span className="text-danger">
                            <i className="ti ti-currency-rupee" />
                          </span>
                        </Link>
                      </div>
                      {/* <p className="fs-12 fw-normal d-flex align-items-center text-truncate">
                                                <span className="text-danger fs-12 d-flex align-items-center me-1">
                                                    <i className="ti ti-arrow-wave-right-up me-1" />
                                                    -10.17%
                                                </span>{" "}
                                                from last week
                                            </p> */}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Total Exponses */}
            {/* Total Exponses */}
            <div className="col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0 pb-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex align-items-center ">
                      <span className="me-2">
                        <i className="ti ti-chart-area-line text-danger" />
                      </span>
                      <h5>Contribution </h5>
                    </div>
                    <div className="dropdown">
                      <Link
                        to="#"
                        className="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                        data-bs-toggle="dropdown"
                      >
                        {selectedYear}
                      </Link>
                      <ul className="dropdown-menu  dropdown-menu-end p-2">
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2023")}
                          >
                            2023
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2024")}
                          >
                            2024
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2025")}
                          >
                            2025
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="#"
                            className="dropdown-item rounded-1"
                            onClick={() => handleYearSelect("2026")}
                          >
                            2026
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="card-body py-0">
                  <ReactApexChart
                    id="expense-analysis"
                    options={expensechart}
                    series={expensechart.series}
                    type="area"
                    height={193}
                  />
                </div>
              </div>
            </div>
            {/* /Total Exponses */}
          </div>
          <div>
            <EmployeeTaxTable data={request} onSearch={handleSearch} />
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default TDS_Reports;
